import { useState } from 'react';

import CreditCardIcon from '@mui/icons-material/CreditCard';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import { Box, Container, Paper, styled } from '@mui/material';
import Button from '@mui/material/Button';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import AddCardModal from './AddCardModal';
import DeleteCardModal from './DeleteCardModal';
import PaymentCardSection from './PaymentCardSection';
import UpdateCardModal from './UpdateCardModal';
import amex from '../../../../assets/card-brands/amex.svg';
import dinersclub from '../../../../assets/card-brands/dinersclub.svg';
import discover from '../../../../assets/card-brands/discover.svg';
import jcb from '../../../../assets/card-brands/jcb.svg';
import mastercard from '../../../../assets/card-brands/mastercard.svg';
import visa from '../../../../assets/card-brands/visa.svg';
import { PAYMENT_TYPES } from '../../../../constants';
import i18n from '../../../../i18n';
import Svg from '../../../elements/svg/Svg';
import Text from '../../../elements/text/Text';
import Title from '../../../elements/title/Title';

const StyledTitleWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const StyledBrandWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginLeft: theme.spacing(2),
  '& > *': {
    marginLeft: theme.spacing(0.5),
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(1),
    alignItems: 'flex-start',
    marginLeft: 0,
  },
}));

const StyledNoBillingContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    justifyContent: 'center',
  },
}));

const StyledInvoiceContainer = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '1em',
  marginBottom: '1em',
  [theme.breakpoints.up('sm')]: {
    width: '500px',
    justifyContent: 'space-between',
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

const stripePromise = loadStripe(import.meta.env.VITE_APP_STRIPE_PUBLIC_KEY);

const PaymentMethod = ({
  billingInfo,
  card,
  handleUpdate,
  handleDelete,
  handleAdd,
  canDelete,
}) => {
  const { t } = useTranslation();

  const [deleteModal, setDeleteModal] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [addModal, setAddModal] = useState(false);

  const locale = i18n.language || 'ja';

  const toggleDeleteModal = () => setDeleteModal(!deleteModal);
  const confirmDelete = async (data) => {
    await handleDelete(data);
    toggleDeleteModal();
  };

  const toggleUpdateModal = () => setUpdateModal(!updateModal);
  const confirmUpdate = async (data) => {
    await handleUpdate(data);
    toggleUpdateModal();
  };

  const toggleAddModal = () => setAddModal(!addModal);
  const confirmAdd = async (data) => {
    await handleAdd(data);
    toggleAddModal();
  };

  const cardsImgList = [amex, dinersclub, discover, jcb, mastercard, visa];

  return (
    <>
      <StyledTitleWrapper>
        <Title subTitle text={t('methodInfo', { ns: 'sectionTitles' })} />
        <StyledBrandWrapper>
          {cardsImgList.map((cardImg, i) => (
            <Svg src={cardImg} key={i} />
          ))}
        </StyledBrandWrapper>
      </StyledTitleWrapper>
      {billingInfo ? (
        <Box>
          {billingInfo.paymentType === PAYMENT_TYPES.INVOICE && (
            <StyledInvoiceContainer variant="outlined">
              <Box sx={{ display: 'flex' }}>
                <ReceiptOutlinedIcon
                  color="disabled"
                  style={{ fontSize: 45 }}
                />
                <Box sx={{ ml: 1 }}>
                  <Text bodyBold>
                    {t('billing.paymentByInvoice', { ns: 'project' })}
                  </Text>
                  <Text component="p" caption style={{ fontStyle: 'italic' }}>
                    {t('billing.noCardRequired', { ns: 'project' })}
                  </Text>
                </Box>
              </Box>
              <Button
                startIcon={<CreditCardIcon />}
                onClick={toggleAddModal}
                disabled={!!card}
              >
                {!card
                  ? t('add', {
                      ns: 'buttons',
                      text: t('billing.creditCard', { ns: 'project' }),
                    })
                  : t('cardOnFile', { ns: 'buttons' })}
              </Button>
            </StyledInvoiceContainer>
          )}
          {billingInfo.paymentType === PAYMENT_TYPES.CARD && (
            <PaymentCardSection
              card={card}
              canDelete={canDelete}
              toggleAddModal={toggleAddModal}
              toggleUpdateModal={toggleUpdateModal}
              toggleDeleteModal={toggleDeleteModal}
            />
          )}
        </Box>
      ) : (
        <StyledNoBillingContainer>
          <InfoOutlinedIcon fontSize="small" sx={{ marginRight: 1 }} />
          <Text>{t('addBillingFirst', { ns: 'descriptions' })}</Text>
        </StyledNoBillingContainer>
      )}

      <DeleteCardModal
        visible={deleteModal}
        onClose={toggleDeleteModal}
        onConfirm={confirmDelete}
      />
      <UpdateCardModal
        card={card}
        visible={updateModal}
        onClose={toggleUpdateModal}
        onConfirm={confirmUpdate}
      />
      <Elements
        stripe={stripePromise}
        options={{ locale: locale }}
        key={locale}
      >
        <AddCardModal
          visible={addModal}
          onClose={toggleAddModal}
          onConfirm={confirmAdd}
        >
          {!canDelete && (
            <Text sx={{ marginBottom: 2 }}>
              {t('cardDeleteWarning', { ns: 'descriptions' })}
            </Text>
          )}
        </AddCardModal>
      </Elements>
    </>
  );
};

PaymentMethod.propTypes = {
  billingInfo: PropTypes.object,
  card: PropTypes.object,
  handleUpdate: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleAdd: PropTypes.func.isRequired,
  canDelete: PropTypes.bool,
};

export default PaymentMethod;
